/**
 * SidebarDndContext — DnD wrapper for sidebar item reordering.
 *
 * This is a SEPARATE DndContext from the task-list DndContext
 * (DndListProvider). The sidebar has its own:
 * - Sensors (mouse + touch with longer delay)
 * - Collision detection (closestCenter)
 * - DragOverlay (simplified sidebar item preview)
 * - handleDragOver (live cross-area visual transfer — mirrors task DnD)
 * - handleDragEnd (privateIndex writes)
 *
 * Architecture: All sidebar dynamic items (Areas, Projects, Lists)
 * are in a single SortableContext. The flat list + positional area
 * derivation means reordering is simply about writing new
 * privateIndex values — area membership is derived from position.
 *
 * ## Parallels with task DnD (GroupedTaskList)
 *
 * | Task DnD concept      | Sidebar equivalent      |
 * |-----------------------|------------------------|
 * | Group (container)     | Area (+ orphan group)  |
 * | Section              | Area header            |
 * | Cross-group transfer  | Move item between areas|
 * | Block drag (section) | Area drag (+ children) |
 *
 * ## Optimistic updates
 * Same pattern as DndListProvider: compute reordered array → call
 * setOptimistic(newStaticData) synchronously → then async DB write.
 * On error: setOptimistic(null) to rollback.
 *
 * ## Drag rules
 * - Areas can be reordered among other areas (children follow as a block)
 * - Projects/Lists can be reordered freely (including across areas)
 * - Dragging an area moves area + all its children as a block
 */

import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core"
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable"
import {
  memo,
  useCallback,
  useContext,
  createContext,
  useRef,
  useState,
  type ReactNode,
  type SetStateAction,
  type Dispatch,
} from "react"
import type { MenuItem, StaticData } from "@/features/static-data/useStaticData"
import { ObjectType } from "@/db/schema"
import {
  distributeIndexes,
  indexNeedsRepair,
  reorderIndex,
} from "@/shared/utils/list/indexUtils"
import {
  reorderSidebarItem,
  bulkReorderSidebar,
  type SidebarReorderItem,
} from "@/db/operations"
import { sidebarDndModifier } from "@/features/dnd/zoomModifier"
import { useViewContext } from "@/app/stores/viewContext"

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Collect an area's children (contiguous non-area items after it). */
function collectAreaBlock(
  menuItems: MenuItem[],
  areaIndex: number,
): MenuItem[] {
  const block: MenuItem[] = [menuItems[areaIndex]!]
  for (let i = areaIndex + 1; i < menuItems.length; i++) {
    if (menuItems[i]!.type === ObjectType.Area) break
    block.push(menuItems[i]!)
  }
  return block
}

/**
 * Get IDs of valid collision targets during an area drag.
 * Only other areas (+ first/last items in the list) can be targets —
 * this prevents the area from snapping to individual children.
 */
function getAreaBoundaryIds(
  menuItems: MenuItem[],
  draggedBlockIds: Set<string>,
): Set<string> {
  const ids = new Set<string>()
  for (const item of menuItems) {
    if (draggedBlockIds.has(item.id)) continue
    if (item.type === ObjectType.Area) ids.add(item.id)
  }
  // Always include first and last non-dragged items as valid targets
  const remaining = menuItems.filter((m) => !draggedBlockIds.has(m.id))
  if (remaining.length > 0) {
    ids.add(remaining[0]!.id)
    ids.add(remaining[remaining.length - 1]!.id)
  }
  return ids
}

/**
 * Schedule a privateIndex repair if precision has collapsed.
 * Lazy-imports indexRepair to avoid bundling it in the hot path.
 */
function scheduleRepairIfNeeded(): void {
  if (!indexNeedsRepair) return
  import("@/db/operations/indexRepair").then(
    async ({ repairPrivateIndexes }) => {
      try {
        const result = await repairPrivateIndexes()
        console.info("[SidebarDnD] Index repair completed:", result)
      } catch (e) {
        console.error("[SidebarDnD] Index repair failed:", e)
      }
    },
  )
}

// ---------------------------------------------------------------------------
// Context — expose dragging state to children (for visual dimming)
// ---------------------------------------------------------------------------

/**
 * Set of IDs being dragged as a block during an area drag.
 * Children of SidebarDndContext can read this to dim companions.
 */
const SidebarDragBlockContext = createContext<Set<string> | null>(null)

/** Hook to check if an item is part of the currently dragged area block. */
export function useSidebarDragBlock(): Set<string> | null {
  return useContext(SidebarDragBlockContext)
}

/**
 * Module-level flag: is sidebar DnD currently active?
 * Read synchronously by MobileDrawer gesture handler to lock swipe-to-close.
 */
export let sidebarDndActive = false

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface SidebarDndContextProps {
  /** All sidebar menuItems in current display order */
  menuItems: MenuItem[]
  /** Full static data (needed for optimistic update) */
  staticData: StaticData
  /** Optimistic setter from useStaticDataImmediate */
  setOptimistic: Dispatch<SetStateAction<StaticData | null>>
  /** Count of later projects not under any area — when >0 the static
   *  "later projects" row is rendered and creates a DnD zone boundary */
  freeStandingLaterCount?: number
  children: ReactNode
}

// ---------------------------------------------------------------------------
// Overlay component — shows a simple label during drag
// ---------------------------------------------------------------------------

const SidebarDragOverlay = memo(function SidebarDragOverlay({
  item,
  count,
}: {
  item: MenuItem | null
  count: number
}) {
  if (!item) return null
  return (
    <div className="rounded-lg bg-primary/10 px-3 py-2 text-base font-medium shadow-lg ring-1 ring-primary/20">
      <span>{item.title || (item.type === ObjectType.Area ? "Area" : "Item")}</span>
      {count > 1 && (
        <span className="ml-2 inline-flex size-5 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
          {count}
        </span>
      )}
    </div>
  )
})

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function SidebarDndContext({
  menuItems,
  staticData,
  setOptimistic,
  freeStandingLaterCount: _freeStandingLaterCount = 0,
  children,
}: SidebarDndContextProps) {
  const [activeItem, setActiveItem] = useState<MenuItem | null>(null)
  /** Set of IDs being dragged as a block (area + children OR multi-select) — state for context */
  const [draggingBlockIds, setDraggingBlockIds] = useState<Set<string> | null>(
    null,
  )
  /** Whether current drag is an area block drag */
  const isAreaDragRef = useRef(false)
  /** Whether current drag is a multi-select drag */
  const isMultiSelectDragRef = useRef(false)
  /** Ref mirror of draggingBlockIds for synchronous collision callback */
  const draggedBlockRef = useRef<Set<string> | null>(null)
  /** Stable ref for items — used in collision callback */
  const itemsRef = useRef(menuItems)
  itemsRef.current = menuItems

  // ── Sidebar selection state ──────────────────────────────────────────
  const selectedSidebarIds = useViewContext((s) => s.selectedSidebarIds)
  const selectedSidebarIdsRef = useRef(selectedSidebarIds)
  selectedSidebarIdsRef.current = selectedSidebarIds

  // ── Sensors ──────────────────────────────────────────────────────────
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 300, tolerance: 5 },
    }),
  )

  // ── Item IDs for SortableContext ────────────────────────────────────
  const itemIds = menuItems.map((m) => m.id)

  // ── Zone boundary ──────────────────────────────────────────────────
  // The "later projects" row is a static visual element between orphan
  // items and the first area. It should NOT restrict drag & drop —
  // users need to freely move projects across it.
  const firstAreaIndex = menuItems.findIndex((m) => m.type === ObjectType.Area)
  const hasBoundary = false
  /** Stable ref so the collision callback can read the boundary */
  const boundaryRef = useRef({ hasBoundary, firstAreaIndex })
  boundaryRef.current = { hasBoundary, firstAreaIndex }

  // ── Custom collision detection ─────────────────────────────────────
  // During area drags, only allow snapping to other areas / list boundaries.
  // This prevents the dragged area from jumping to individual children.
  // During multi-select drags, filter out all selected items from targets.
  const sidebarCollision: typeof closestCenter = useCallback((args) => {
    // Multi-select: filter out all selected items (they move as a group)
    if (isMultiSelectDragRef.current && draggedBlockRef.current) {
      const blockIds = draggedBlockRef.current
      const filtered = args.droppableContainers.filter(
        (c) => !blockIds.has(c.id as string) || c.id === args.active.id,
      )
      return closestCenter({ ...args, droppableContainers: filtered })
    }

    if (isAreaDragRef.current && draggedBlockRef.current) {
      const boundaryIds = getAreaBoundaryIds(
        itemsRef.current,
        draggedBlockRef.current,
      )
      // Include active item so "return to sender" works
      boundaryIds.add(args.active.id as string)
      const filtered = args.droppableContainers.filter((c) =>
        boundaryIds.has(c.id as string),
      )
      return closestCenter({ ...args, droppableContainers: filtered })
    }

    // Zone boundary constraint — prevent dragging across the "later projects" row
    const { hasBoundary, firstAreaIndex } = boundaryRef.current
    if (hasBoundary) {
      const items = itemsRef.current
      const activeId = args.active.id as string
      const dragIdx = items.findIndex((m) => m.id === activeId)
      if (dragIdx !== -1) {
        const isInOrphanZone = dragIdx < firstAreaIndex
        const filtered = args.droppableContainers.filter((c) => {
          const cIdx = items.findIndex((m) => m.id === (c.id as string))
          if (cIdx === -1) return true // unknown container, allow
          // Constrain to same zone
          return isInOrphanZone ? cIdx < firstAreaIndex : cIdx >= firstAreaIndex
        })
        return closestCenter({ ...args, droppableContainers: filtered })
      }
    }

    return closestCenter(args)
  }, [])

  // ── Drag start — track active item for overlay ─────────────────────
  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      sidebarDndActive = true
      const id = event.active.id as string
      const item = menuItems.find((m) => m.id === id) ?? null
      setActiveItem(item)

      // Multi-select drag: if the dragged item is part of the selection
      const selIds = selectedSidebarIdsRef.current
      if (selIds.length > 1 && selIds.includes(id)) {
        isMultiSelectDragRef.current = true
        isAreaDragRef.current = false
        const blockSet = new Set(selIds)
        draggedBlockRef.current = blockSet
        setDraggingBlockIds(blockSet)
        return
      }

      isMultiSelectDragRef.current = false

      // Track area block drag
      if (item?.type === ObjectType.Area) {
        isAreaDragRef.current = true
        const idx = menuItems.findIndex((m) => m.id === item.id)
        if (idx !== -1) {
          const block = collectAreaBlock(menuItems, idx)
          const blockSet = new Set(block.map((b) => b.id))
          draggedBlockRef.current = blockSet
          setDraggingBlockIds(blockSet)
        }
      } else {
        isAreaDragRef.current = false
        draggedBlockRef.current = null
        setDraggingBlockIds(null)
      }
    },
    [menuItems],
  )

  // ── Drag end — optimistic update + DB persist ──────────────────────
  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      sidebarDndActive = false
      setActiveItem(null)
      const wasMultiSelect = isMultiSelectDragRef.current
      isAreaDragRef.current = false
      isMultiSelectDragRef.current = false
      draggedBlockRef.current = null
      setDraggingBlockIds(null)

      const { active, over } = event
      if (!over || active.id === over.id) return

      const activeId = active.id as string
      const overId = over.id as string

      const oldIndex = menuItems.findIndex((m) => m.id === activeId)
      const overIndex = menuItems.findIndex((m) => m.id === overId)
      if (oldIndex === -1 || overIndex === -1) return

      const draggedItem = menuItems[oldIndex]!

      // ── Multi-select drag: move all selected items as a group ──
      if (wasMultiSelect) {
        await handleMultiSelectDrag(
          menuItems,
          selectedSidebarIdsRef.current,
          overId,
          staticData,
          setOptimistic,
        )
        scheduleRepairIfNeeded()
        return
      }

      // ── Zone boundary safety guard ─────────────────────────────
      // Prevent single-item drags across the "later projects" row
      if (hasBoundary && draggedItem.type !== ObjectType.Area) {
        const dragInOrphan = oldIndex < firstAreaIndex
        const overInOrphan = overIndex < firstAreaIndex
        if (dragInOrphan !== overInOrphan) return // cross-zone → no-op
      }

      // ── Area drag: move area + its children as a block ─────────
      if (draggedItem.type === ObjectType.Area) {
        await handleAreaDrag(
          menuItems,
          oldIndex,
          overIndex,
          staticData,
          setOptimistic,
        )
        scheduleRepairIfNeeded()
        return
      }

      // ── Single item drag (Project/List) ────────────────────────
      await handleItemDrag(
        menuItems,
        oldIndex,
        overIndex,
        staticData,
        setOptimistic,
      )
      scheduleRepairIfNeeded()
    },
    [menuItems, staticData, setOptimistic],
  )

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={sidebarCollision}
      modifiers={[sidebarDndModifier]}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SidebarDragBlockContext.Provider value={draggingBlockIds}>
        <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
          {children}
        </SortableContext>
      </SidebarDragBlockContext.Provider>
      <DragOverlay dropAnimation={null} modifiers={[sidebarDndModifier]}>
        <SidebarDragOverlay item={activeItem} count={draggingBlockIds?.size ?? 1} />
      </DragOverlay>
    </DndContext>
  )
}

// ---------------------------------------------------------------------------
// handleItemDrag — single project/list reorder
//
// Uses reorderIndex() — the same helper that task DnD (GroupedTaskList) uses
// for intra-group reorder. This ensures consistent midpoint calculation
// regardless of drag direction.
// ---------------------------------------------------------------------------

async function handleItemDrag(
  menuItems: MenuItem[],
  fromIndex: number,
  toIndex: number,
  staticData: StaticData,
  setOptimistic: Dispatch<SetStateAction<StaticData | null>>,
): Promise<void> {
  const item = menuItems[fromIndex]!

  // Use reorderIndex — identical algorithm to task DnD intra-group reorder.
  // It creates a "without" array, looks up neighbors at toIndex, and calls
  // nextIndex(prev, next) for the midpoint. Handles both drag-up and
  // drag-down correctly.
  const newIdx = reorderIndex(menuItems, fromIndex, toIndex, (m) => m.sortIndex)

  // Optimistic update — arrayMove semantics (same as @dnd-kit/sortable arrayMove)
  const reordered = Array.from(menuItems)
  reordered.splice(toIndex, 0, reordered.splice(fromIndex, 1)[0]!)
  // Patch the moved item's sortIndex for consistency
  const movedIdx = reordered.findIndex((m) => m.id === item.id)
  if (movedIdx !== -1) {
    reordered[movedIdx] = { ...reordered[movedIdx]!, sortIndex: newIdx }
  }
  setOptimistic({ ...staticData, menuItems: reordered })

  // DB persist (async)
  try {
    const result = await reorderSidebarItem(item.id, newIdx)
    if (!result.ok) {
      console.error("[SidebarDnD]", result.error)
      setOptimistic(null)
    }
  } catch {
    setOptimistic(null) // rollback
  }
}

// ---------------------------------------------------------------------------
// handleAreaDrag — area + children move as a block
//
// Mirrors the "section block drag" pattern from task DnD:
// 1. Collect area + all children (contiguous non-area items)
// 2. Remove the block from the list
// 3. Find the correct insertion point in the "without" array
// 4. Distribute indexes evenly for the whole block
// 5. Optimistic update + DB persist
// ---------------------------------------------------------------------------

async function handleAreaDrag(
  menuItems: MenuItem[],
  fromIndex: number,
  toIndex: number,
  staticData: StaticData,
  setOptimistic: Dispatch<SetStateAction<StaticData | null>>,
): Promise<void> {
  // 1. Collect area + its children
  const block = collectAreaBlock(menuItems, fromIndex)
  const blockIds = new Set(block.map((b) => b.id))

  // 2. Build list without the block
  const without = menuItems.filter((m) => !blockIds.has(m.id))

  // 3. Find insertion point.
  // The overItem is the item at toIndex in the ORIGINAL array.
  // If it's part of our block, find the nearest non-block item instead.
  let overItem = menuItems[toIndex]
  if (overItem && blockIds.has(overItem.id)) {
    overItem = undefined
    if (toIndex > fromIndex) {
      for (let i = fromIndex + block.length; i < menuItems.length; i++) {
        if (!blockIds.has(menuItems[i]!.id)) {
          overItem = menuItems[i]
          break
        }
      }
    } else {
      for (let i = fromIndex - 1; i >= 0; i--) {
        if (!blockIds.has(menuItems[i]!.id)) {
          overItem = menuItems[i]
          break
        }
      }
    }
    // If we still don't have a valid over item, no-op
    if (!overItem) return
  }

  let insertAt: number
  if (!overItem) {
    insertAt = toIndex > fromIndex ? without.length : 0
  } else {
    const overInWithout = without.findIndex((m) => m.id === overItem!.id)
    if (overInWithout === -1) {
      insertAt = toIndex > fromIndex ? without.length : 0
    } else if (toIndex > fromIndex) {
      insertAt = overInWithout + 1
    } else {
      insertAt = overInWithout
    }
  }

  // 4. Compute boundary indexes and distribute
  const prevIdx = insertAt > 0 ? without[insertAt - 1]?.sortIndex : undefined
  const nextIdx =
    insertAt < without.length ? without[insertAt]?.sortIndex : undefined
  const newIndexes = distributeIndexes(prevIdx, nextIdx, block.length)

  // 5. Optimistic update
  const reordered = Array.from(without)
  const updatedBlock = block.map((item, i) => ({
    ...item,
    sortIndex: newIndexes[i]!,
  }))
  reordered.splice(insertAt, 0, ...updatedBlock)
  setOptimistic({ ...staticData, menuItems: reordered })

  // 6. DB persist
  const items: SidebarReorderItem[] = block.map((item, i) => ({
    id: item.id,
    newIndex: newIndexes[i]!,
  }))

  try {
    const result = await bulkReorderSidebar(items)
    if (!result.ok) {
      console.error("[SidebarDnD] area drag failed:", result.error)
      setOptimistic(null)
    }
  } catch {
    setOptimistic(null) // rollback
  }
}

// ---------------------------------------------------------------------------
// handleMultiSelectDrag — move all selected items as a group to target position
//
// Similar to handleAreaDrag but the "block" is the set of selected items
// (which may not be contiguous). Their relative order is preserved.
// ---------------------------------------------------------------------------

async function handleMultiSelectDrag(
  menuItems: MenuItem[],
  selectedIds: string[],
  overId: string,
  staticData: StaticData,
  setOptimistic: Dispatch<SetStateAction<StaticData | null>>,
): Promise<void> {
  const selectedSet = new Set(selectedIds)

  // Collect selected items in their current relative order
  const block = menuItems.filter((m) => selectedSet.has(m.id))
  if (block.length === 0) return

  // Build list without selected items
  const without = menuItems.filter((m) => !selectedSet.has(m.id))

  // Find insertion point: where the over-item is in the "without" list
  const overInWithout = without.findIndex((m) => m.id === overId)
  // Insert AFTER the over-item (drop target)
  const insertAt = overInWithout === -1 ? without.length : overInWithout + 1

  // Compute boundary indexes and distribute
  const prevIdx = insertAt > 0 ? without[insertAt - 1]?.sortIndex : undefined
  const nextIdx =
    insertAt < without.length ? without[insertAt]?.sortIndex : undefined
  const newIndexes = distributeIndexes(prevIdx, nextIdx, block.length)

  // Optimistic update
  const reordered = Array.from(without)
  const updatedBlock = block.map((item, i) => ({
    ...item,
    sortIndex: newIndexes[i]!,
  }))
  reordered.splice(insertAt, 0, ...updatedBlock)
  setOptimistic({ ...staticData, menuItems: reordered })

  // DB persist
  const items: SidebarReorderItem[] = block.map((item, i) => ({
    id: item.id,
    newIndex: newIndexes[i]!,
  }))

  try {
    const result = await bulkReorderSidebar(items)
    if (!result.ok) {
      console.error("[SidebarDnD] multi-select drag failed:", result.error)
      setOptimistic(null)
    }
  } catch {
    setOptimistic(null) // rollback
  }
}
